import {
  type EquipmentDocument,
  type EquipmentDocumentDto,
  type EquipmentField,
  type EquipmentFieldDto,
  type EquipmentTypeField,
  type EquipmentTypeFieldDto,
  type SingleEquipment,
  type SingleEquipmentCategory,
  type SingleEquipmentCategoryDto,
  type SingleEquipmentDto,
  type SingleEquipmentType,
  type SingleEquipmentTypeDto,
  type CreateUpdateEquipmentDto,
  type CreateUpdateEquipmentFormFields,
  EquipmentTypeFieldType,
} from './equipment.types';

export type EquipmentFactsPatch = Partial<
  Pick<CreateUpdateEquipmentDto, 'installed' | 'running_hours'>
>;

export const mapDtoToEquipmentCategory = ({
  created_at,
  id,
  name,
  updated_at,
  equipment_count,
}: SingleEquipmentCategoryDto): SingleEquipmentCategory => ({
  createdAt: created_at,
  updatedAt: updated_at,
  id,
  name,
  equipment_count,
});

export const mapDtoToEquipmentType = ({
  category_id,
  created_at,
  updated_at,
  category,
  items,
  ...values
}: SingleEquipmentTypeDto): SingleEquipmentType => ({
  ...values,
  categoryId: category_id,
  createdAt: created_at,
  updatedAt: updated_at,
  category: mapDtoToEquipmentCategory(category),
  fields: items?.map(mapDtoToEquipmentTypeField) ?? [],
});

export const mapDtoToEquipmentField = ({ item_id, value }: EquipmentFieldDto): EquipmentField => ({
  itemId: item_id,
  value,
});

export const mapDtoToEquipmentDocument = ({
  id,
  name,
  link,
  content_type,
  size,
}: EquipmentDocumentDto): EquipmentDocument => ({
  id,
  name,
  link,
  contentType: content_type,
  size,
});

export const mapDtoToEquipment = ({
  created_at,
  type_id,
  vessel_id,
  updated_at,
  type,
  structure_values,
  is_ai_generated,
  documents,
  crew_notes,
  running_hours,
  installed,
  ...values
}: SingleEquipmentDto): SingleEquipment => ({
  ...values,
  createdAt: created_at,
  typeId: type_id,
  updated_at: updated_at,
  vesselId: vessel_id,
  type: mapDtoToEquipmentType(type),
  fields: structure_values?.map(mapDtoToEquipmentField) ?? [],
  isAiGenerated: is_ai_generated,
  documents: documents?.map(mapDtoToEquipmentDocument) ?? [],
  crewNotes: crew_notes ?? '',
  installed: installed ?? null,
  runningHours: running_hours ?? null,
});

export const mapDtoToEquipmentTypeField = ({
  created_at,
  equipment_type_id,
  field_type,
  is_required,
  sort_order,
  validation_rules,
  updated_at,
  ...field
}: EquipmentTypeFieldDto): EquipmentTypeField => ({
  ...field,
  createdAt: created_at,
  equipmentTypeId: equipment_type_id,
  fieldType: field_type,
  isRequired: is_required,
  sortOrder: sort_order,
  updatedAt: updated_at,
  validationRules: validation_rules,
});

export const mapCreateUpdateEquipmentToDto = ({
  fields,
  name,
  typeId,
  installed,
  runningHours,
}: CreateUpdateEquipmentFormFields): CreateUpdateEquipmentDto => ({
  name,
  structure_values: Object.entries(fields).map(([item_id, value]) => ({ item_id, value })),
  type_id: typeId,
  installed: installed || null,
  running_hours: runningHours ?? null,
});

/**
 * The item as its own update body, for the two facts the item's page edits in
 * place. The save is a PUT that rewrites every column it names, so the reading
 * being changed rides on top of everything the item already holds rather than
 * travelling alone — otherwise saving hours would blank the install date.
 */
export const mapEquipmentToUpdateDto = (
  { name, typeId, fields, installed, runningHours }: SingleEquipment,
  patch: EquipmentFactsPatch = {},
): CreateUpdateEquipmentDto => ({
  name,
  type_id: typeId,
  structure_values: fields.map(({ itemId, value }) => ({ item_id: itemId, value })),
  installed,
  running_hours: runningHours,
  ...patch,
});

const typeFieldsDefaultValues: Record<
  EquipmentTypeFieldType,
  null | undefined | string | number | boolean
> = {
  [EquipmentTypeFieldType.BOOLEAN]: false,
  [EquipmentTypeFieldType.FLOAT]: undefined,
  [EquipmentTypeFieldType.INTEGER]: undefined,
  [EquipmentTypeFieldType.LIST_FLOAT]: null,
  [EquipmentTypeFieldType.LIST_INTEGER]: null,
  [EquipmentTypeFieldType.STRING]: '',
  [EquipmentTypeFieldType.LIST_STRING]: '',
  [EquipmentTypeFieldType.LONG_STRING]: '',
};

export const mapUpdateEquipmentDefaultValues = (
  fields: EquipmentTypeField[],
  equipment: SingleEquipment,
): CreateUpdateEquipmentFormFields => {
  const fieldsValues = fields?.reduce((acc, { id, fieldType }) => {
    return {
      ...acc,
      [id]:
        equipment.fields.find(({ itemId }) => itemId === id)?.value ??
        typeFieldsDefaultValues[fieldType],
    };
  }, {});
  return {
    fields: fieldsValues,
    name: equipment.name,
    typeId: equipment.typeId,
    installed: equipment.installed,
    runningHours: equipment.runningHours,
  };
};

export const mapCreateEquipmentDefaultValues = (
  typeId?: null | string,
  fields?: EquipmentTypeField[],
): CreateUpdateEquipmentFormFields => {
  const fieldsValues =
    fields?.reduce((acc, { id, fieldType }) => {
      return {
        ...acc,
        [id]: typeFieldsDefaultValues[fieldType],
      };
    }, {}) ?? {};
  return {
    fields: fieldsValues,
    name: '',
    typeId: typeId ?? '',
    installed: null,
    runningHours: null,
  };
};
